import { z } from 'zod';

import {
  ADVANCE_TYPES,
  CONTRACT_STATUSES,
  CONTRACT_TYPES,
  PAYOUT_PERIODS,
  PERSON_DOCUMENT_TYPES,
  PERSON_KINDS,
  PERSON_STATUSES,
  TAX_MODES,
} from '../enums';
import {
  zColor,
  zCurrency,
  zCustomFields,
  zDayOfMonth,
  zISODate,
  zId,
  zMoneyNonNegative,
  zNonEmptyString,
  zOptionalString,
  zPagination,
  zSort,
  zTaxRate,
} from './common';

/* ─── Команды ───────────────────────────────────────────────────────────── */

export const createTeamSchema = z.object({
  name: zNonEmptyString(60),
  game: zOptionalString(60),
  color: zColor.default('#6366F1'),
});
export type CreateTeamInput = z.infer<typeof createTeamSchema>;

export const updateTeamSchema = createTeamSchema.partial().extend({
  isArchived: z.boolean().optional(),
});

/* ─── Люди ──────────────────────────────────────────────────────────────── */

export const createPersonSchema = z.object({
  kind: z.enum(PERSON_KINDS).default('PLAYER'),
  nickname: zNonEmptyString(40),

  firstName: zOptionalString(60),
  lastName: zOptionalString(60),
  middleName: zOptionalString(60),
  birthDate: zISODate.nullish(),
  country: zOptionalString(60),
  city: zOptionalString(60),
  timezone: zOptionalString(60),

  teamId: zId.nullish(),
  discipline: zOptionalString(40),
  roleTitle: zOptionalString(60),
  status: z.enum(PERSON_STATUSES).default('ACTIVE'),
  joinedAt: zISODate.nullish(),
  leftAt: zISODate.nullish(),

  phone: zOptionalString(40),
  telegram: zOptionalString(60),
  discord: zOptionalString(60),
  email: zOptionalString(120),
  steam: zOptionalString(120),

  passportInfo: zOptionalString(300),
  bankDetails: zOptionalString(300),
  cryptoWallet: zOptionalString(120),

  notes: zOptionalString(2000),
  customFields: zCustomFields.optional(),
});
export type CreatePersonInput = z.infer<typeof createPersonSchema>;

export const updatePersonSchema = createPersonSchema.partial();
export type UpdatePersonInput = z.infer<typeof updatePersonSchema>;

export const personFilterSchema = zPagination.merge(zSort).extend({
  kind: z.enum(PERSON_KINDS).optional(),
  status: z.enum(PERSON_STATUSES).optional(),
  teamId: zId.optional(),
  discipline: z.string().trim().max(40).optional(),
  search: z.string().trim().max(60).optional(),
  includeArchived: z.coerce.boolean().default(false),
});
export type PersonFilter = z.infer<typeof personFilterSchema>;

/* ─── Контракты ─────────────────────────────────────────────────────────── */

export const createContractSchema = z
  .object({
    personId: zId,
    number: zOptionalString(40),
    title: zNonEmptyString(120),
    type: z.enum(CONTRACT_TYPES).default('SALARY'),
    status: z.enum(CONTRACT_STATUSES).default('ACTIVE'),

    startDate: zISODate,
    endDate: zISODate.nullish(),
    autoRenew: z.boolean().default(false),

    salaryMinor: zMoneyNonNegative,
    currency: zCurrency,
    payoutPeriod: z.enum(PAYOUT_PERIODS).default('MONTHLY'),
    payoutDay: zDayOfMonth.default(10),

    advanceEnabled: z.boolean().default(false),
    advanceType: z.enum(ADVANCE_TYPES).default('PERCENT'),
    advanceValue: z.number().min(0).default(40),
    advanceDay: zDayOfMonth.default(25),

    taxMode: z.enum(TAX_MODES).default('NO_TAX'),
    taxRate: zTaxRate.default(0),

    notes: zOptionalString(2000),
  })
  .refine((value) => !value.endDate || value.endDate >= value.startDate, {
    message: 'validation.endBeforeStart',
    path: ['endDate'],
  })
  .refine(
    (value) => value.advanceType !== 'PERCENT' || !value.advanceEnabled || value.advanceValue <= 100,
    { message: 'validation.maxPercent', path: ['advanceValue'] },
  );
export type CreateContractInput = z.infer<typeof createContractSchema>;

export const updateContractSchema = z.object({
  number: zOptionalString(40),
  title: zNonEmptyString(120).optional(),
  type: z.enum(CONTRACT_TYPES).optional(),
  status: z.enum(CONTRACT_STATUSES).optional(),
  startDate: zISODate.optional(),
  endDate: zISODate.nullish(),
  autoRenew: z.boolean().optional(),
  salaryMinor: zMoneyNonNegative.optional(),
  payoutPeriod: z.enum(PAYOUT_PERIODS).optional(),
  payoutDay: zDayOfMonth.optional(),
  advanceEnabled: z.boolean().optional(),
  advanceType: z.enum(ADVANCE_TYPES).optional(),
  advanceValue: z.number().min(0).optional(),
  advanceDay: zDayOfMonth.optional(),
  taxMode: z.enum(TAX_MODES).optional(),
  taxRate: zTaxRate.optional(),
  notes: zOptionalString(2000),
  /** Дата, с которой действует новая ставка — попадёт в историю зарплаты. */
  salaryEffectiveFrom: zISODate.optional(),
  salaryChangeReason: zOptionalString(200),
});
export type UpdateContractInput = z.infer<typeof updateContractSchema>;

export const terminateContractSchema = z.object({
  terminatedAt: zISODate,
  reason: zOptionalString(300),
});

/* ─── Документы человека (F-17) ─────────────────────────────────────────── */

export const createPersonDocumentSchema = z.object({
  type: z.enum(PERSON_DOCUMENT_TYPES).default('OTHER'),
  title: zNonEmptyString(120),
  number: zOptionalString(60),
  issuedAt: zISODate.nullish(),
  expiresAt: zISODate.nullish(),
  reminderDaysBefore: z.number().int().min(1).max(180).default(30),
  notes: zOptionalString(500),
});
export type CreatePersonDocumentInput = z.infer<typeof createPersonDocumentSchema>;

/* ─── Ответы ────────────────────────────────────────────────────────────── */

export interface TeamDto {
  id: string;
  name: string;
  game: string | null;
  color: string;
  isArchived: boolean;
  peopleCount: number;
}

export interface PersonListItemDto {
  id: string;
  kind: string;
  nickname: string;
  fullName: string | null;
  age: number | null;
  avatarPath: string | null;
  teamId: string | null;
  teamName: string | null;
  discipline: string | null;
  roleTitle: string | null;
  status: string;
  country: string | null;
  city: string | null;

  /** Действующий контракт, если он есть. */
  contractId: string | null;
  salaryMinor: number | null;
  currency: string | null;
  taxMode: string | null;
  taxRate: number | null;

  /** Ближайшая выплата по контракту. */
  nextPayoutDate: string | null;
  nextPayoutMinor: number | null;

  /** Сколько человеку ещё не выплачено (F-01). */
  owedMinor: number;
  isOverdue: boolean;
}

export interface PersonDetailDto extends PersonListItemDto {
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;
  birthDate: string | null;
  timezone: string | null;
  joinedAt: string | null;
  leftAt: string | null;

  phone: string | null;
  telegram: string | null;
  discord: string | null;
  email: string | null;
  steam: string | null;

  /** Чувствительные поля приходят только участникам с правами администратора. */
  passportInfo: string | null;
  bankDetails: string | null;
  cryptoWallet: string | null;
  canSeeSensitive: boolean;

  notes: string | null;
  customFields: Record<string, string | number | boolean | null>;

  createdAt: string;
}

export interface ContractFileDto {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

export interface SalaryChangeDto {
  id: string;
  oldSalaryMinor: number;
  newSalaryMinor: number;
  effectiveFrom: string;
  reason: string | null;
  createdAt: string;
}

export interface ContractDto {
  id: string;
  personId: string;
  personNickname: string;
  number: string | null;
  title: string;
  type: string;
  status: string;

  startDate: string;
  endDate: string | null;
  autoRenew: boolean;
  /** Сколько дней осталось до окончания; null — бессрочный. */
  daysUntilEnd: number | null;

  salaryMinor: number;
  currency: string;
  payoutPeriod: string;
  payoutDay: number;

  advanceEnabled: boolean;
  advanceType: string;
  advanceValue: number;
  advanceDay: number;
  advanceMinor: number;
  remainderMinor: number;

  taxMode: string;
  taxRate: number;
  /** Разложенный расчёт по текущей ставке. */
  taxMinor: number;
  netMinor: number;
  orgCostMinor: number;

  nextPayoutDate: string | null;
  terminatedAt: string | null;
  terminationReason: string | null;
  notes: string | null;

  files: ContractFileDto[];
  salaryHistory: SalaryChangeDto[];
  createdAt: string;
}

export interface PersonDocumentDto {
  id: string;
  type: string;
  title: string;
  number: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  reminderDaysBefore: number;
  daysUntilExpiry: number | null;
  fileName: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  notes: string | null;
}

/** Взаиморасчёты с человеком (F-01). */
export interface PersonSettlementDto {
  accruedMinor: number;
  paidMinor: number;
  owedMinor: number;
  overpaidMinor: number;
  /** Прочие расходы на человека, не являющиеся зарплатой (F-04). */
  personCostsMinor: number;
  /** Полная стоимость человека для организации за всё время. */
  totalCostMinor: number;
  currency: string;
}

export interface PersonListResponse {
  items: PersonListItemDto[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}
